import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { HubEditorial } from "@/components/site/HubEditorial";
import { HUB_EDITORIAL } from "@/data/hub-editorial";
import { socialImageMeta } from "@/lib/social";
import { ShareBar } from "@/components/site/ShareBar";
import { makeSeoDescription } from "@/lib/seo-description";

const CANONICAL = "https://lifewestway.com/credit-cards";
const TITLE = "Credit Cards for Expats 2026 | LifeWestWay";

export const Route = createFileRoute("/credit-cards")({
  head: () => ({
    meta: [
      { title: TITLE },
      {
        name: "description",
        content:
          makeSeoDescription("Build local credit fast: the best newcomer, secured and rewards credit cards for expats in the USA, UK, Australia, Canada, Italy & Portugal (2026)."),
      },
      { property: "og:title", content: TITLE },
      { property: "og:site_name", content: "LifeWestWay" },
      {
        property: "og:description",
        content:
          makeSeoDescription("Six countries, six 2026 credit-card playbooks — newcomer cards, secured options, rewards & how to build a local credit score from day one."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta(),
      { name: "twitter:title", content: TITLE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: CreditCardsHub,
});

type Guide = {
  country: string;
  flag: string;
  accent: string;
  title: string;
  blurb: string;
  highlight: string;
  href: string;
};

const GUIDES: Guide[] = [
  {
    country: "United States",
    flag: "🇺🇸",
    accent: "from-sky-500/25 to-indigo-400/10",
    title: "Best Credit Cards for New Immigrants in the US (2026)",
    blurb:
      "SSN vs ITIN, secured cards from Self, OpenSky & Capital One, plus Nova Credit and Amex Credit Passport shortcuts for translating foreign credit history.",
    highlight: "SSN / ITIN / no-doc paths",
    href: "/usa/best-credit-cards-new-immigrants-usa-2026",
  },
  {
    country: "United Kingdom",
    flag: "🇬🇧",
    accent: "from-indigo-500/25 to-sky-400/10",
    title: "Best UK Credit Cards for Expats & Students (2026)",
    blurb:
      "How to build a UK credit file, thin-file friendly issuers, and the top rewards cards once you're on the electoral roll.",
    highlight: "Thin-file friendly issuers",
    href: "/uk",
  },
  {
    country: "Australia",
    flag: "🇦🇺",
    accent: "from-amber-500/25 to-emerald-400/10",
    title: "Best Credit Cards for New Arrivals in Australia (2026)",
    blurb:
      "Comprehensive Credit Reporting, visa-holder eligibility, low-fee starter cards and premium rewards for permanent residents.",
    highlight: "Visa-holder eligible cards",
    href: "/australia",
  },
  {
    country: "Canada",
    flag: "🇨🇦",
    accent: "from-red-500/25 to-rose-400/10",
    title: "Best Newcomer Credit Cards in Canada (2026)",
    blurb:
      "No-credit-history newcomer programs from RBC, Scotiabank, TD and CIBC — plus how to graduate to premium rewards in 12 months.",
    highlight: "Newcomer programs, no credit needed",
    href: "/canada",
  },
  {
    country: "Italy",
    flag: "🇮🇹",
    accent: "from-emerald-500/25 to-rose-500/10",
    title: "Best Credit & Debit Cards for Expats in Italy (2026)",
    blurb:
      "Carta di credito vs prepagata, Codice Fiscale requirements, and the digital banks (Revolut, N26) that expats actually use.",
    highlight: "Codice Fiscale required",
    href: "/italy",
  },
  {
    country: "Portugal",
    flag: "🇵🇹",
    accent: "from-emerald-500/25 to-amber-400/10",
    title: "Best Credit Cards for Expats in Portugal (2026)",
    blurb:
      "NIF setup, Portuguese bank cards vs Revolut/Wise, and how D7/D8 visa holders get approved for their first Portuguese credit line.",
    highlight: "NIF + proof of residence",
    href: "/portugal",
  },
];

function CreditCardsHub() {
  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: "Credit Cards" },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card/40 to-background/60 p-8 sm:p-12">
        <span
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-3xl"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl"
        />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            6 Countries · Updated 2026
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Credit Cards for <span className="text-accent">Expats & Newcomers</span>
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-foreground/75">
            Build a local credit score from day one — newcomer cards, secured options and
            premium rewards across all six Western destinations, updated for 2026.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section
        aria-label="Credit card guides by country"
        className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
      >
        {GUIDES.map((g) => (
          <Link title={`Read the credit card guide: ${g.title}`}
            key={g.title}
            to={g.href}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-6 transition-all hover:border-accent/60 hover:bg-card/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span
              aria-hidden
              className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${g.accent} opacity-0 transition-opacity group-hover:opacity-100`}
            />
            <div className="relative flex items-center justify-between">
              <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/40 px-3 py-1 text-xs font-semibold text-foreground/80">
                <span aria-hidden className="text-base leading-none">
                  {g.flag}
                </span>
                {g.country}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent/90">
                2026
              </span>
            </div>

            <h2 className="relative mt-4 font-display text-xl font-bold tracking-tight text-foreground group-hover:text-accent">
              {g.title}
            </h2>
            <p className="relative mt-3 text-sm text-foreground/70">{g.blurb}</p>

            <div className="relative mt-5 flex items-center justify-between border-t border-border/50 pt-4">
              <span className="text-xs font-medium text-foreground/60">
                <span className="text-accent">◆</span> {g.highlight}
              </span>
              <span className="text-sm font-semibold text-accent">
                Explore <span aria-hidden>→</span>
              </span>
            </div>
          </Link>
        ))}
      </section>

      <HubEditorial data={HUB_EDITORIAL["credit-cards"]} />

      <ShareBar />
    </main>
  );
}
